import SpeakButton from "../EnglishTool/SpeakButton";

export const chapterPages = {
  1: [
    {
      title: <h2>Nouns Review & New Types</h2>,
      body: (
        <>
          <p>
            A <b>noun</b> names a person, place, thing, or idea.
            <SpeakButton text="A noun names a person, place, thing, or idea." />
          </p>
          <ul>
            <li>Person: <i>teacher, Sam</i><SpeakButton text="Teacher, Sam" /></li> 
            <li>Place: <i>library, Paris</i><SpeakButton text="Library, Paris" /></li> 
            <li>Thing: <i>book, bicycle</i><SpeakButton text="Book, Bicycle" /></li> 
            <li>Idea: <i>happiness, freedom</i><SpeakButton text="Happiness, Freedom" /></li> 
          </ul>
        </>
      ),
    },
    {
      title: <h2>Collective Nouns</h2>,
      body: (
        <>
          <p>
            <b>Collective nouns</b> refer to a group of people, animals, or things as a single unit.
            <SpeakButton text="Collective nouns refer to a group of people, animals, or things as a single unit." />
          </p>
          <ul>
            <li>A <b>team</b> of players <SpeakButton text="A Team of players" /></li> 
            <li>A <b>flock</b> of birds <SpeakButton text="A flock of birds" /></li> 
            <li>A <b>class</b> of students <SpeakButton text="A class of students" /></li> 
          </ul>
          <p>
            Example: “The <b>crowd</b> cheered loudly.”
            <SpeakButton text="The crowd cheered loudly." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Possessive Nouns</h2>,
      body: (
        <>
          <p>
            <b>Possessive nouns</b> show that something belongs to someone or something.
            <SpeakButton text="Possessive nouns show that something belongs to someone or something." />
          </p>
          <ul>
            <li>
              Add <b>’s</b> to a singular noun: <i>the dog’s bone</i>
              <SpeakButton text="the dog's bone" />
            </li>
            <li>
              Add just <b>’</b> to a plural noun that ends in s: <i>the students’ books</i>
              <SpeakButton text="the students' books" />
            </li>
          </ul>
          <p>
            Example: “This is <b>Mary’s</b> pencil.”{" "}
            <SpeakButton text="This is Mary's pencil." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Nouns in Context</h2>,
      body: (
        <>
          <p>
            <b>Tip:</b> A word can be a noun in some sentences and not in others. Look for the role it plays!
          </p>
          <p>
            Example: <i>The <b>team</b> celebrates their victory.</i>
            <SpeakButton text="The team celebrates their victory." />
          </p>
          <p>
            Is “team” a singular or plural noun here?
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Find the Nouns</h2>,
      body: (
        <>
          <p>
            Underline the nouns in this sentence:
            <br />
            <b>
              “The children’s group won the award for their school.”
              <SpeakButton text="The children's group won the award for their school." />
            </b>
          </p>
          <p>
            <i>Can you find a possessive and a collective noun?</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Make it Possessive</h2>,
      body: (
        <>
          <p>
            Change this to show possession:
            <br />
            <b>The backpack belongs to Mike.</b>
            <SpeakButton text="The backpack belongs to Mike." />
          </p>
          <p>
            <i>Type: “Mike’s backpack.”</i>
          </p>
        </>
      ),
    },
  ],

  2: [
    {
      title: <h2>What Are Verbs? Quick Review</h2>,
      body: (
        <>
          <p>
            A <b>verb</b> is a word that shows an action or a state of being.
            <SpeakButton text="A verb is a word that shows an action or a state of being." />
          </p>
          <ul>
            <li>Action: <i>run, jump, write</i><SpeakButton text="run, jump, write" /></li>
            <li>State: <i>is, am, was, are</i><SpeakButton text="is, am, was, are" /></li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Subject-Verb Agreement</h2>,
      body: (
        <>
          <p>
            A verb must <b>match</b> its subject in number (singular/plural).
            <SpeakButton text="A verb must match its subject in number." />
          </p>
          <ul>
            <li>She <b>runs</b>. (singular) <SpeakButton text="She runs" /></li>
            <li>They <b>run</b>. (plural)<SpeakButton text="They run" /></li>
          </ul>
          <p>
            <b>Tricky:</b> “The group <b>is</b> ready.” (Group is singular.)
            <SpeakButton text="The group is ready." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Progressive Tenses</h2>,
      body: (
        <>
          <p>
            <b>Progressive tenses</b> show something is happening over time.
            <SpeakButton text="Progressive tenses show something is happening over time." />
          </p>
          <ul>
            <li>Present: She <b>is eating</b> lunch now. <SpeakButton text="She is eating lunch now" /></li>
            <li>Past: She <b>was eating</b> lunch at noon.<SpeakButton text="She was eating lunch at noon" /></li>
            <li>Future: She <b>will be eating</b> lunch soon.<SpeakButton text="She will be eating lunch soon" /></li>
          </ul>
          <p>
            <i>Look for “am/is/are/was/were/will be” + verb-ing.</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Pick the Correct Verb</h2>,
      body: (
        <>
          <p>
            Choose the right verb:
            <br />
            <b>
              “The team (is/are) practicing for the game.”
              <SpeakButton text="The team is practicing for the game." />
            </b>
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Change the Tense</h2>,
      body: (
        <>
          <p>
            Change to past progressive:
            <br />
            <b>They play outside.</b>
            <SpeakButton text="They play outside." />
          </p>
          <p>
            <i>Type: “They were playing outside.”</i>
            <SpeakButton text="They were playing outside." />
          </p>
        </>
      ),
    },
  ],

  3: [
    {
      title: <h2>Adjectives Review & Order</h2>,
      body: (
        <>
          <p>
            <b>Adjectives</b> describe nouns. You can use more than one!
            <SpeakButton text="Adjectives describe nouns. You can use more than one adjective." />
          </p>
          <p>
            <b>Order:</b> Number &rarr; Opinion &rarr; Size &rarr; Age &rarr; Color &rarr; Origin &rarr; Material &rarr; Noun
          </p>
          <p>
            Example: “Three beautiful large old red French wooden chairs.”
            <SpeakButton text="Three beautiful large old red French wooden chairs." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Comparative and Superlative</h2>,
      body: (
        <>
          <p>
            <b>Comparative</b> compares two things: <b>-er</b> or <b>more</b>
          </p>
          <p>
            <b>Superlative</b> compares three or more: <b>-est</b> or <b>most</b>
          </p>
          <ul>
            <li>tall &rarr; <b>taller</b> (comparative) &rarr; <b>tallest</b> (superlative) <SpeakButton text="Tall, Taller, Tallest" /></li>
            <li>beautiful &rarr; <b>more beautiful</b> &rarr; <b>most beautiful</b><SpeakButton text="beautiful, more beautiful, most beautiful" /></li>
          </ul>
          <p>
            Irregular: good &rarr; <b>better</b> &rarr; <b>best</b> <SpeakButton text="good, better, best" />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Order the Adjectives</h2>,
      body: (
        <>
          <p>
            Put the adjectives in the correct order:
            <br />
            <b>green / big / dragon / Chinese</b>
            <SpeakButton text="green, big, dragon, Chinese" />
          </p>
          <p>
            <i>Answer: “big green Chinese dragon”</i>
            <SpeakButton text="big green Chinese dragon" />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Change to Superlative</h2>,
      body: (
        <>
          <p>
            Change to superlative:
            <br />
            <b>This book is good.</b>
            <SpeakButton text="This book is good." />
          </p>
          <p>
            <i>Type: “This book is the best.”</i>
            <SpeakButton text="This book is the best." />
          </p>
        </>
      ),
    },
  ],

  4: [
    {
      title: <h2>What Are Adverbs?</h2>,
      body: (
        <>
          <p>
            <b>Adverbs</b> describe verbs, adjectives, or other adverbs.
            <SpeakButton text="Adverbs describe verbs, adjectives, or other adverbs." />
          </p>
          <p>
            Many adverbs answer: <b>How?</b> <b>When?</b> <b>Where?</b> <b>How often? <SpeakButton text="How? When? Where? How often?" /></b>
          </p>
          <ul>
            <li>quickly, softly, always, there, yesterday <SpeakButton text="quickly, softly, always, there, yesterday" /></li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Frequency & Comparison</h2>,
      body: (
        <>
          <p>
            <b>Frequency adverbs</b> tell how often:
            <br />
            always, usually, sometimes, rarely, never
            <SpeakButton text="always, usually, sometimes, rarely, never" />
          </p>
          <p>
            <b>Comparing adverbs:</b> fast, faster, fastest &middot; well, better, best
            <SpeakButton text="fast, faster, fastest, well, better, best" />
          </p>
        </>
      ),
    },
    {
      title: <h2>Placement & Special Adverbs</h2>,
      body: (
        <>
          <p>
            Adverbs can go at the start, middle, or end of a sentence.
            <br />
            <i>
              “Usually, I walk to school.”<SpeakButton text="Usually, I walk to school." /><br />
              “I usually walk to school.” <SpeakButton text="I usually walk to school." /><br />
              “I walk to school usually.”<SpeakButton text="I walk to school usually" />
            </i>
          </p>
          <p>
            Not all adverbs end with “-ly”: fast, hard, late.
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Fill the Blank</h2>,
      body: (
        <>
          <p>
            Fill in the blank:
            <br />
            “She speaks ______ (quick).”
            <SpeakButton text="She speaks quick." />
          </p>
          <p>
            <i>Type: “She speaks quickly.”</i>
            <SpeakButton text="She speaks quickly." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Use an Adverb</h2>,
      body: (
        <>
          <p>
            Change “good” to an adverb:
            <br />
            “He runs good.”
            <SpeakButton text="He runs good." />
          </p>
          <p>
            <i>Type: “He runs well.”</i>
            <SpeakButton text="He runs well." />
          </p>
        </>
      ),
    },
  ],

  5: [
    {
      title: <h2>Reading Longer Passages</h2>,
      body: (
        <>
          <p>
            Grade 4 passages have <b>more than one paragraph</b>. Find the main idea in each part!
          </p>
          <p>
            <b>Example:</b>
            <br />
            <i>
              “Sophie likes helping her classmates. One day, she saw her friend looking sad. Sophie sat beside her and listened.”
            </i>
            <SpeakButton text="Sophie likes helping her classmates. One day, she saw her friend looking sad. Sophie sat beside her and listened." />
          </p>
          <p>
            <i>What is the main idea?</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Inference & Context Clues</h2>,
      body: (
        <>
          <p>
            <b>Inference</b> means using clues to understand things not directly said.
            <SpeakButton text="Inference means using clues to understand things not directly said." />
          </p>
          <p>
            <b>Context clues</b> are words or sentences around a new word that help you guess its meaning.
            <SpeakButton text="Context clues are words or sentences around a new word that help you guess its meaning" />
          </p>
          <p>
            <b>Example:</b>
            <br />
            “He grabbed his umbrella as dark clouds appeared.”
            <SpeakButton text="He grabbed his umbrella as dark clouds appeared." />
            <br />
            <i>What can you guess will happen?</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Main Idea & Inference</h2>,
      body: (
        <>
          <p>
            Read the passage:
            <br />
            <i>
              “After the concert, everyone applauded and cheered. The performers bowed and smiled, happy with their show.”
            </i>
            <SpeakButton text="After the concert, everyone applauded and cheered. The performers bowed and smiled, happy with their show." />
          </p>
          <ul>
            <li>What is the main idea of this passage?</li>
            <li>How do you know the performers were happy?</li>
          </ul>
        </>
      ),
    },
  ],
};